type THREE_NS = typeof import('three');
type Obj3D = import('three').Object3D;

// MEASURING A MESH BY ITS SHAPE, where its box says only how far it reaches.
//
// A box is one number per axis and hides the shape inside it: feet 45,3 cm apart under a
// backrest 48,2 cm wide is the same box as a straight-sided chair 48,2 wide. The reading
// here gets past that, in the part's OWN frame — the holder's, millimetres once the scene
// has scaled the mesh, x the width, y the depth, z the height — so turning the part on the
// bench changes nothing about what "width" means.

// THE BOTTOM AND THE TOP OF A WIDTH AND A DEPTH, as a drawing prints them.
export type Ends = { xBottom: number; xTop: number; yBottom: number; yTop: number };

// HOW DEEP "THE BOTTOM" AND "THE TOP" GO: a tenth of the height each. A drawing does not say
// where its top width was taken, only that it is up there — a chair's backrest is widest a
// little below its rounded upper edge, and a slice at the very top reads narrower than the
// figure printed for it. The widest point within the band is that figure; the price is a
// product whose "top" feature sits lower than a tenth down.
export const END_BAND = 0.1;

// The widest extent of the mesh within the lowest and the highest END_BAND of its height,
// across and along. `box` is the part's extent in the holder's frame — the same box the scene
// draws — so the bands are cut against the box on screen. Null for a mesh with no height.
//
// Off the VERTICES, once, when the part loads: a band's extent is the extent of the points in
// it, and a quarter of a million vertices is a few milliseconds.
export function measureEnds(
  THREE: THREE_NS, holder: Obj3D, body: Obj3D, box: import('three').Box3,
): Ends | null {
  const tall = box.max.z - box.min.z;
  if (!(tall > 0)) return null;
  const lowTo = box.min.z + tall * END_BAND;
  const highFrom = box.max.z - tall * END_BAND;
  const empty = () => ({ x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity });
  const low = empty();
  const high = empty();
  const grow = (b: ReturnType<typeof empty>, x: number, y: number) => {
    if (x < b.x0) b.x0 = x;
    if (x > b.x1) b.x1 = x;
    if (y < b.y0) b.y0 = y;
    if (y > b.y1) b.y1 = y;
  };

  // Every vertex into the holder's frame: the file's own nesting (an orientation answer is a
  // node with a turn on it), then the scene's scale and centring, and nothing of the pose.
  holder.updateMatrixWorld(true);
  const toHolder = new THREE.Matrix4().copy(holder.matrixWorld).invert();
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  body.traverse((node) => {
    const mesh = node as import('three').Mesh;
    const pos = mesh.isMesh ? mesh.geometry?.getAttribute('position') : null;
    if (!pos) return;
    m.multiplyMatrices(toHolder, mesh.matrixWorld);
    for (let i = 0; i < pos.count; i += 1) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m);
      if (v.z <= lowTo) grow(low, v.x, v.y);
      if (v.z >= highFrom) grow(high, v.x, v.y);
    }
  });
  if (!Number.isFinite(low.x0) || !Number.isFinite(high.x0)) return null;
  return {
    xBottom: low.x1 - low.x0, xTop: high.x1 - high.x0,
    yBottom: low.y1 - low.y0, yTop: high.y1 - high.y0,
  };
}
